/**
 * Utility functions for currency and number conversions
 */

/**
 * Parse any value (string, number, null, undefined) to a number
 * Handles currency strings with $ and commas
 */
export const parseNumber = (value: string | number | null | undefined): number => {
    if (value == null) {
        return 0;
    }
    if (typeof value === "number") {
        return Number.isNaN(value) ? 0 : value;
    }
    // Remove currency symbols and commas
    const cleaned = value.replace(/[$,]/g, "");
    const num = Number.parseFloat(cleaned);
    return Number.isNaN(num) ? 0 : num;
};

/**
 * Format a number as currency string with thousands separators
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
    if (value == null) {
        return "$0.00";
    }
    // Strip $ and commas if it's a string
    const numValue =
        typeof value === "string" ? Number.parseFloat(value.replace(/[$,]/g, "")) : value;
    if (Number.isNaN(numValue)) {
        return "$0.00";
    }
    return numValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * Parse value to currency string (for display in input fields)
 */
export const parseValueToCurrency = (val: string | number | null | undefined): string => {
    if (val == null) {
        return "$0.00";
    }
    const num = typeof val === "string" ? Number.parseFloat(val) : val;
    return Number.isNaN(num) ? "$0.00" : `$${num.toFixed(2)}`;
};

/**
 * Safe division that returns 0 if divisor is 0
 */
export const safeDivide = (numerator: number, denominator: number): number => {
    if (denominator === 0) {
        return 0;
    }
    return numerator / denominator;
};

/**
 * Calculate percentage of a value
 */
export const calculatePercentage = (value: number, percentage: number): number => {
    return (value * percentage) / 100;
};
